import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, palette } from '@m12s/component-library';
import { useDispatch, useSelector } from 'react-redux';
import {
  Body,
  Buttons,
  Dialog,
  Header,
  HeaderIcon,
  Title,
} from 'components/Dialog';
import _startCase from 'lodash/startCase';
import { signIn } from 'lib/icons';
import { H5, P, Text } from 'components/Text';
import styled from 'styled-components';
import {
  useEmbeddedAuthorizations,
  useEmbeddedEffect,
} from '@machinemetrics/mm-react-embeddable';
import { Drawer, DrawerBody } from 'components/Drawer';
import { actionSetAuthorizationModalOpen } from 'lib/actions';
import { getIsAuthorizationModalOpen } from 'lib/selectors/getIsAuthorizationModalOpen';
import { createLoginUrl } from 'lib/utils/request';

const HStack = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 2rem;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1rem 0;
`;

// OV runs in the shoppulse-kiosk WebView, which cannot open windows: the legacy interactive step is a
// full-screen drawer iframe, so legacy apps must be same-domain. Identity comes from the broker;
// scopes are not listed.
const EmbeddableAuthorizationModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isModalOpen = useSelector(getIsAuthorizationModalOpen);
  const { pending, approve, dismiss } = useEmbeddedAuthorizations();
  const [loginAuthStale, setLoginAuthStale] = useState(false);
  const [selectedPayload, setSelectedPayload] = useState(null);
  const [busy, setBusy] = useState(false);
  const [unchecked, setUnchecked] = useState(() => {
    return new Set();
  });

  const brokered = pending.filter((entry) => {
    return !!entry.consent;
  });
  const legacy = pending.filter((entry) => {
    return !entry.consent;
  });
  // A lone row is the decision, whatever was unchecked earlier (no checkbox is shown for it).
  const isChecked = (entry) => {
    return brokered.length === 1 || !unchecked.has(entry.origin);
  };
  const checkedRows = brokered.filter(isChecked);

  // Per-row choices belong to one showing of the dialog; this component lives for the app.
  useEffect(() => {
    if (!pending.length) {
      setUnchecked(new Set());
    }
  }, [pending.length]);

  const toggle = (entry) => {
    setUnchecked((prev) => {
      const next = new Set(prev);
      if (next.has(entry.origin)) {
        next.delete(entry.origin);
      } else {
        next.add(entry.origin);
      }
      return next;
    });
  };

  const onApprove = () => {
    if (!checkedRows.length) {
      return;
    }
    setBusy(true);
    brokered
      .filter((entry) => {
        return !isChecked(entry);
      })
      .forEach((entry) => {
        return dismiss(entry);
      });
    Promise.allSettled(
      checkedRows.map((entry) => {
        return Promise.resolve(approve(entry));
      })
    ).finally(() => {
      setBusy(false);
    });
  };

  const onCancel = () => {
    if (busy) return;
    pending.forEach((entry) => {
      return dismiss(entry);
    });
  };

  // Other surfaces suppress themselves while authorization is pending.
  useEffect(() => {
    dispatch(actionSetAuthorizationModalOpen(pending.length > 0));
  }, [pending.length, dispatch]);

  useEffect(() => {
    if (
      selectedPayload &&
      !pending.some((entry) => {
        return entry.origin === selectedPayload.origin;
      })
    ) {
      setSelectedPayload(null);
    }
  }, [pending, selectedPayload]);

  // A `login` signal also means OV's own session may be stale; this dialog ends in a full reload.
  useEmbeddedEffect('*', 'login', () => {
    setLoginAuthStale(true);
  });

  useEmbeddedEffect('*', 'authorize-failed', () => {
    setSelectedPayload(null);
  });

  const renderConsentBody = (entry) => {
    const client = entry.consent.client || {};
    return (
      <div>
        <H5
          style={{ margin: '0 0 0.5rem 0' }}
          data-testid="embedded-consent-name"
        >
          {client.name || entry.origin}
        </H5>
        <Text data-testid="embedded-consent-hostname">
          {client.hostname || entry.origin}
        </Text>
        {entry.consent.stepUp && (
          <P data-testid="embedded-consent-stepup">
            {t('Requesting additional access.')}
          </P>
        )}
        {entry.error && (
          <P data-testid="embedded-consent-error">
            {t('Authorization could not be completed. Try again.')}
          </P>
        )}
      </div>
    );
  };

  return (
    <>
      <Dialog isOpen={loginAuthStale}>
        <Header>
          <Title color={palette.White}>
            {_startCase(t(`authorization required`))}
          </Title>
          <HeaderIcon color={palette.White} icon={signIn} />
        </Header>
        <Body>
          <P>
            {t(
              'Your authorization has expired. Please reauthorize to continue.'
            )}
          </P>
        </Body>
        <Buttons>
          <Button
            onClick={() => {
              window.location.href = createLoginUrl();
            }}
          >
            {_startCase(t('reauthorize'))}
          </Button>
        </Buttons>
      </Dialog>
      {/* Escape dismisses (nothing is persisted) except while an approval is in flight; an overlay tap does not. */}
      <Dialog
        isOpen={isModalOpen}
        onRequestClose={onCancel}
        shouldCloseOnOverlayClick={false}
      >
        <Header>
          <Title color={palette.White}>
            {_startCase(t(`authorization required`))}
          </Title>
          <HeaderIcon color={palette.White} icon={signIn} />
        </Header>
        <Body>
          {pending.length === 0 && <P>{t(`You're ready to go!`)}</P>}
          {brokered.map((entry) => {
            return (
              <Row key={entry.origin} data-testid="embedded-consent">
                {brokered.length > 1 ? (
                  <Checkbox
                    id={`embedded-consent-${entry.origin}`}
                    data-testid="embedded-consent-check"
                    inputProps={{
                      checked: isChecked(entry),
                      disabled: busy,
                      onChange: () => {
                        return toggle(entry);
                      },
                    }}
                  >
                    {renderConsentBody(entry)}
                  </Checkbox>
                ) : (
                  renderConsentBody(entry)
                )}
              </Row>
            );
          })}
          {legacy.length > 0 && (
            <P>
              {t(
                'The following apps require authorization. Authorize each app below to continue.'
              )}
            </P>
          )}
          {legacy.map((entry) => {
            // A framed login must open from the user's click on this row.
            return (
              <HStack
                style={{ padding: '1rem 0' }}
                key={entry.origin}
                data-testid="embedded-legacy"
              >
                {/* minWidth 0 lets a long origin wrap instead of squeezing the button. */}
                <div style={{ marginRight: '2rem', minWidth: 0, flexGrow: 1 }}>
                  <H5 style={{ margin: '0 0 1rem 0', wordBreak: 'break-all' }}>
                    {entry.origin}
                  </H5>
                </div>
                <div style={{ display: 'flex', flexShrink: 0 }}>
                  <Button
                    style={{ margin: 'auto' }}
                    onClick={() => {
                      setSelectedPayload(entry);
                    }}
                  >
                    {_startCase(t(`authorize`))}
                  </Button>
                </div>
              </HStack>
            );
          })}
        </Body>
        {pending.length > 0 && (
          <Buttons>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={onCancel}
              data-testid="embedded-consent-cancel"
            >
              {_startCase(t('cancel'))}
            </Button>
            {brokered.length > 0 && (
              <Button
                disabled={busy || !checkedRows.length}
                onClick={onApprove}
                data-testid="embedded-consent-approve"
              >
                {_startCase(t('approve'))}
              </Button>
            )}
          </Buttons>
        )}
      </Dialog>
      <Drawer isOpen={selectedPayload !== null}>
        <DrawerBody style={{ padding: '0' }}>
          {/* The framed login may run, keep its origin and submit forms; it may not open windows,
              navigate the tablet away, or raise dialogs. */}
          <iframe
            title="authorize"
            sandbox="allow-scripts allow-same-origin allow-forms"
            style={{ border: 'none', width: '100%', height: '100%' }}
            src={selectedPayload?.authUrl}
          />
        </DrawerBody>
      </Drawer>
    </>
  );
};

export { EmbeddableAuthorizationModal };
